import { connect } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';

import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnoOrganizationFunctionSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnoOrganizationFunctionSelectOption';
import { IKnmSupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';
import { IKnoOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnoOrganisationSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { KNM_CONTROL_AGENCY } from 'CorePackage/MainModule/routes/routes';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  COLLABORATING_KNO_NAME_BLOCK_ID,
  INSPECTORS_TITLE_ID,
  KNO_ORGANIZATION_FUNCTIONS_TITLE_ID,
  getCollaboratingOrganizationBlockId,
  getInspectorBlockId,
  KNO_ORGANIZATION_BLOCK_ID,
  SUPERVISION_TYPE_BLOCK_ID,
  getInspectorErvkBlockId,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import {
  getKnmErpParticipantPositionsSelectOptions,
  getKnmFormRef,
  getKnmKnoOrganizationFunctionsSelectOptions,
  getKnmSupervisionTypesSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { getCatalogKnmInspectorTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmInspectorTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmInspectorTypeSelectOption';
import { IKnmErpForm } from 'KnmErpPackage/MainModule/interfaces/IKnmErpForm';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IParticipantPosition } from 'APIPackage/DictionariesModule/interfaces/IParticipantPosition';

import { IKnmErpFormInspector } from '../interfaces/IKnmErpFormInspector';
import { IKnmErpKnoOrganizationForm } from '../interfaces/IKnmErpKnoOrganizationForm';

interface IKnmErpKnoOrganizationProps {
  knoOrganisationsSelectOptions: IUserOrganisationSelectOption[] | undefined;
  statusCode: IKnmStatus['code'] | undefined;
  watchOnly: boolean;
  settings: IAdminSetting[];
  knmCreatedDate: string | null | undefined;
}

interface IKnmErpKnoOrganization {
  isProsecutorOrSuperProsecutor: boolean;
  formRef: IKnmFormRef | undefined;
  knmSupervisionTypesSelectOptions: IKnmSupervisionTypeSelectOption[] | undefined;
  knmInspectorTypesSelectOptions: IKnmInspectorTypeSelectOption[];
  knoOrganizationFunctionsSelectOptions: IKnoOrganizationFunctionSelectOption[] | undefined;
  positionsSelectOptions: IDictionarySelectOption<IParticipantPosition>[] | undefined;
}

const KnmErpKnoOrganizationWithoutStore = ({
  knoOrganisationsSelectOptions,
  statusCode,
  watchOnly,
  knmCreatedDate,
  formRef,
  isProsecutorOrSuperProsecutor,
  knmInspectorTypesSelectOptions,
  knmSupervisionTypesSelectOptions,
  knoOrganizationFunctionsSelectOptions,
  settings,
  positionsSelectOptions,
}: IKnmErpKnoOrganizationProps & IKnmErpKnoOrganization) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const isUseERVKParamTables = () =>
    isLaterThanAdminSetting(knmCreatedDate, settings, AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM);

  const inspectorsFieldName = isUseERVKParamTables() ? 'inspectorsErvk' : 'inspectors';

  const handleKnoOrganizationSelectChange = useCallback(() => {
    formRef?.current.change('collaboratingOrganizations', undefined);
    formRef?.current.change('collaboratingKnoName', undefined);
    formRef?.current.change('knoOrganizationFunctions', undefined);
    formRef?.current.change(inspectorsFieldName, undefined);
  }, [formRef]);

  const addInspector = useCallback(() => !watchOnly && push && push(inspectorsFieldName, { listId: uuidv4() }), [
    push,
    watchOnly,
  ]);
  const addCollaboratingOrganization = useCallback(
    () => !watchOnly && push && push('collaboratingOrganizations', { listId: uuidv4() }),
    [push, watchOnly],
  );
  const addRegistryFunctionNumber = useCallback(
    () => !watchOnly && push && push('knoOrganizationFunctions', undefined),
    [push, watchOnly],
  );

  const makeRemoveControlOrganizationHandler = (index: number) => () =>
    !watchOnly && remove && remove('collaboratingOrganizations', index);
  const makeRemoveCommissionersHandler = (index: number) => () =>
    !watchOnly && remove && remove(inspectorsFieldName, index);
  const makeRemoveRegistryFunctionHandler = (index: number) => () =>
    !watchOnly && remove && remove('knoOrganizationFunctions', index);

  const handleFullNameChange = (index: number) => () => {
    formRef?.current.change(`${inspectorsFieldName}[${index}].position` as keyof IKnmErpForm);
    formRef?.current.change(`${inspectorsFieldName}[${index}].type` as keyof IKnmErpForm);
  };

  const isHideKnoOrganizationFunctionsField = isLaterThanAdminSetting(
    knmCreatedDate,
    settings,
    AdminSettingCodesEnum.ERP_NEW_FIELDS_FZ68_4_1_36,
  );

  return (
    <Section title="Информация об органе контроля" id={KNM_CONTROL_AGENCY}>
      {!isUseERVKParamTables() && (
        <Row>
          <FieldBlock label="Наименование органа контроля" id={KNO_ORGANIZATION_BLOCK_ID}>
            <SelectInputField
              name="knoOrganization"
              options={knoOrganisationsSelectOptions}
              isClearable
              isSearchable
              disabled={(!isProsecutorOrSuperProsecutor && Boolean(statusCode)) || watchOnly}
              size="medium"
              onChange={handleKnoOrganizationSelectChange}
            />
          </FieldBlock>
        </Row>
      )}

      <FieldBlock
        label="Уполномоченные на проведение проверки"
        id={INSPECTORS_TITLE_ID}
        button={
          <Button
            className="shared-btn-add"
            type="button"
            variant="link"
            color="primary"
            text="Добавить"
            disabled={watchOnly}
            onClick={addInspector}
          />
        }
      >
        <FieldArray name={inspectorsFieldName}>
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((name, index) => {
              const fieldValue: IKnmErpFormInspector = fields.value?.[index];
              const id = isUseERVKParamTables()
                ? getInspectorErvkBlockId(fieldValue?.listId)
                : getInspectorBlockId(fieldValue?.listId);

              return (
                <Row key={id} id={id} isListItem margin="medium" notWrap>
                  {isUseERVKParamTables() && (
                    <Row>
                      <FieldBlock size="col-3">
                        <TextareaField
                          name={`${name}.fullName`}
                          placeholder="ФИО"
                          disabled={watchOnly}
                          validate={makeRequired(REQUIRED_ERROR)}
                        />
                      </FieldBlock>
                      <FieldBlock size="col-3">
                        <SelectInputField
                          name={`${name}.position`}
                          isClearable
                          isSearchable
                          placeholder="Должность"
                          options={positionsSelectOptions}
                          disabled={watchOnly}
                          size="medium"
                          validate={makeRequired(REQUIRED_ERROR)}
                        />
                      </FieldBlock>
                    </Row>
                  )}
                  {!isUseERVKParamTables() && (
                    <Row>
                      <FieldBlock size="col-3">
                        <TextareaField
                          name={`${name}.fullName`}
                          size="medium"
                          disabled={watchOnly}
                          onChange={handleFullNameChange(index)}
                        />
                      </FieldBlock>
                      <FieldBlock size="col-3">
                        <TextareaField name={`${name}.position`} placeholder="Должность" disabled={watchOnly} />
                      </FieldBlock>
                      <FieldBlock size="col-3">
                        <SelectInputField
                          name={`${name}.type`}
                          placeholder="Тип проверяющего"
                          options={knmInspectorTypesSelectOptions}
                          isClearable
                          disabled={watchOnly}
                          size="medium"
                        />
                      </FieldBlock>
                    </Row>
                  )}
                  <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveCommissionersHandler(index)} />
                </Row>
              );
            });
          }}
        </FieldArray>
      </FieldBlock>

      {!isUseERVKParamTables() && (
        <Row>
          <FieldBlock label="Вид государственного контроля (надзора)" id={SUPERVISION_TYPE_BLOCK_ID}>
            <SelectInputField
              name="supervisionType"
              options={knmSupervisionTypesSelectOptions}
              isSearchable
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>
      )}

      <Row>
        <FieldBlock
          label="Наименование органа контроля (надзора), органа муниципального контроля, с которым КНМ проводится совместно
              (в соответствии с шаблоном плана)"
          id={COLLABORATING_KNO_NAME_BLOCK_ID}
        >
          <TextareaField className="shared-field" name="collaboratingKnoName" disabled={watchOnly} />
        </FieldBlock>
      </Row>

      <FieldBlock
        label="Наименование органа контроля (надзора), органа муниципального контроля, с которым КНМ проводится совместно (в
          соответствии со справочником ФРГУ)"
        button={
          <Button
            className="shared-btn-add"
            type="button"
            variant="link"
            color="primary"
            text="Добавить"
            disabled={watchOnly}
            onClick={addCollaboratingOrganization}
          />
        }
      >
        <FieldArray name="collaboratingOrganizations">
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((name, index) => {
              const collaboratingOrganizationsValue:
                | IKnmErpKnoOrganizationForm['collaboratingOrganizations']
                | undefined = fields.value;
              const collaboratingOrganizationFieldValue = collaboratingOrganizationsValue?.[index];
              const id = getCollaboratingOrganizationBlockId(collaboratingOrganizationFieldValue.listId);
              const collaboratingOrganizationsSelectOptions:
                | IKnoOrganisationSelectOption[]
                | undefined = knoOrganisationsSelectOptions?.map(knoOrganisationSelectOption => ({
                ...knoOrganisationSelectOption,
                isDisabled: collaboratingOrganizationsValue?.some(
                  collaboratingOrganizationValue =>
                    collaboratingOrganizationValue?.organization?.id === knoOrganisationSelectOption.id &&
                    knoOrganisationSelectOption.id !== collaboratingOrganizationFieldValue?.organization?.id,
                ),
              }));

              return (
                <Row key={id}>
                  <SelectInputField
                    className="shared-deleting-row"
                    name={`${name}.organization`}
                    placeholder="Наименование органа контроля"
                    options={collaboratingOrganizationsSelectOptions}
                    isClearable
                    isSearchable
                    disabled={watchOnly}
                    size="medium"
                  />
                  <CloseButton
                    className="shared-deleting-row-btn"
                    onClick={makeRemoveControlOrganizationHandler(index)}
                  />
                </Row>
              );
            });
          }}
        </FieldArray>
      </FieldBlock>

      {!isHideKnoOrganizationFunctionsField && (
        <FieldBlock
          label="Указание на реестровый номер функции в федеральной государственной информационной системе Федеральный реестр
            государственных и муниципальных услуг (функции)"
          button={
            <Button
              className="shared-btn-add"
              type="button"
              variant="link"
              color="primary"
              text="Добавить"
              disabled={watchOnly}
              onClick={addRegistryFunctionNumber}
            />
          }
          id={KNO_ORGANIZATION_FUNCTIONS_TITLE_ID}
        >
          <FieldArray name="knoOrganizationFunctions">
            {({ fields }) => {
              if (!fields.length) {
                return null;
              }

              return fields.map((name, index) => (
                <Row key={name}>
                  <SelectInputField
                    className="shared-deleting-row"
                    name={name}
                    options={knoOrganizationFunctionsSelectOptions}
                    isClearable
                    isSearchable
                    disabled={watchOnly}
                  />
                  <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveRegistryFunctionHandler(index)} />
                </Row>
              ));
            }}
          </FieldArray>
        </FieldBlock>
      )}
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpKnoOrganization => ({
  formRef: getKnmFormRef(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  knmSupervisionTypesSelectOptions: getKnmSupervisionTypesSelectOptions(state),
  knmInspectorTypesSelectOptions: getCatalogKnmInspectorTypesSelectOptions(state),
  knoOrganizationFunctionsSelectOptions: getKnmKnoOrganizationFunctionsSelectOptions(state),
  positionsSelectOptions: getKnmErpParticipantPositionsSelectOptions(state),
});

export const KnmErpKnoOrganization = connect(mapStateToProps)(KnmErpKnoOrganizationWithoutStore);
